import type { ReactNode } from 'react';
import { Filter } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import { Badge } from '../badge';
import { Button } from '../button';
import css from './filters-popover.module.scss';

const styles = pickCss(css, css.root);

type FiltersPopoverProps = {
  // Active-filter count — shows a badge on the trigger and highlights it.
  count?: number;
  // Trigger label (default "Filters"). Pass an empty string for an
  // icon-only trigger (just the icon + count badge).
  label?: string;
  // Panel edge alignment relative to the trigger.
  align?: 'start' | 'end';
  // Trigger button size (default 'lg').
  size?: 'sm' | 'default' | 'lg';
  // Filter controls rendered inside the popover panel.
  children: ReactNode;
};

// Generic "Filters" trigger + popover panel: a button (with an active-count
// badge) that toggles an anchored panel holding the secondary filter
// controls. Closes on outside click (backdrop) or Escape. Used by list
// toolbars to keep the primary filters inline and tuck the rest away.
export const FiltersPopover = ({
  count = 0,
  label = 'Filters',
  align = 'start',
  size = 'lg',
  children,
}: FiltersPopoverProps) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <div className={styles({ isActive: count > 0 })}>
      <Button
        icon={<Filter style={{ height: 14, width: 14 }} />}
        onClick={() => setOpen((prev) => !prev)}
        size={size}
        type="button"
        variant="outline"
      >
        {label}
        {count > 0 && (
          <Badge className={css.rootBadge} variant="active">
            {count}
          </Badge>
        )}
      </Button>
      {open && (
        <>
          <button
            aria-label="Close filters"
            className={css.rootBackdrop}
            onClick={close}
            type="button"
          />
          <div
            className={joinCss(
              css.rootPanel,
              align === 'end' && css.rootPanelEnd
            )}
            role="dialog"
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
};
